import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, fmtDate, slugToLabel } from "@/components/common";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose,
} from "@/components/ui/drawer";
import {
  Phone, MessageCircle, ArrowLeft, Loader2, Mail, MapPin, Tag, Clock,
  Tags, MessageSquare, CalendarClock, Activity, X, Check, Copy, ChevronRight, ShieldAlert, Video,
} from "lucide-react";
import { toast } from "sonner";

const QUICK_REMARKS = ["Customer interested", "Call back later", "Details shared", "Wants webinar", "Asked for more info", "Not interested", "Busy", "Number switched off"];

export default function TeleLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sheet, setSheet] = useState(null); // status | remark | followup | whatsapp
  const [remark, setRemark] = useState("");
  const [fuNote, setFuNote] = useState("");
  const [fuWhen, setFuWhen] = useState(null); // Date
  const [webinars, setWebinars] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get(`/leads/${id}`), api.get("/lead-statuses")]);
      setD(r.data); setStatuses(s.data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate("/app");
    }
  }, [id, navigate]);
  useEffect(() => { load(); api.get("/whatsapp-templates").then((r) => setTemplates(r.data.items)).catch(() => {}); api.get("/webinars/mine/upcoming").then((r) => setWebinars(r.data.items)).catch(() => {}); }, [load]);

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  const { lead, activities, remarks, followups } = d;
  const sm = statuses.find((s) => s.slug === lead.current_status);

  const call = async () => { try { await api.post(`/leads/${id}/call`); } catch {} window.location.href = `tel:${lead.mobile}`; };
  const openWa = async (tplId) => {
    try {
      const { data } = await api.post(`/leads/${id}/whatsapp`, tplId ? { template_id: tplId } : {});
      if (data.whatsapp_url) window.open(data.whatsapp_url, "_blank");
      setSheet(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyMsg = async (tplId) => {
    try {
      const { data } = await api.post(`/leads/${id}/whatsapp`, tplId ? { template_id: tplId } : {});
      await navigator.clipboard.writeText(data.message || "");
      toast.success("Message copied");
    } catch { toast.error("Couldn't copy"); }
  };
  const changeStatus = async (slug) => {
    try { await api.patch(`/leads/${id}/status`, { status_slug: slug }); toast.success(`Status: ${slugToLabel(slug)}`); setSheet(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addRemark = async (text) => {
    const val = (text || remark).trim();
    if (!val) return;
    try { await api.post(`/leads/${id}/remarks`, { text: val }); setRemark(""); toast.success("Remark saved"); setSheet(null); load(); }
    catch (e) { toast.error("Couldn't save remark. Please try again."); }
  };
  const saveFollowup = async () => {
    if (!fuWhen) return toast.error("Pick a time");
    try {
      await api.post(`/leads/${id}/followups`, { scheduled_at: fuWhen.toISOString(), note: fuNote });
      toast.success(`Follow-up set · ${fmtDate(fuWhen.toISOString())}`);
      setFuNote(""); setFuWhen(null); setSheet(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const completeFu = async (fid) => { try { await api.patch(`/followups/${fid}`, { status: "COMPLETED" }); toast.success("Follow-up completed"); load(); } catch {} };
  const addToWebinar = async (wid, wname) => {
    try {
      const { data } = await api.post(`/webinars/${wid}/register-lead`, { lead_id: id });
      if (data.added) { toast.success(`Added to ${data.webinar}`); if (lead.current_status !== "webinar-ready") { try { await api.patch(`/leads/${id}/status`, { status_slug: "webinar-ready" }); } catch {} } }
      else toast.info(data.message || "Already registered");
      setSheet(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickWhen = (kind) => {
    const dt = new Date();
    if (kind === "tomorrow") dt.setDate(dt.getDate() + 1);
    dt.setHours(11, 0, 0, 0);
    setFuWhen(dt);
  };

  return (
    <div className="pb-24">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-400 mb-3" data-testid="back-button"><ArrowLeft className="h-4 w-4" />Back</button>

      {/* Header */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-heading text-xl font-extrabold">{lead.name}</h1>
          <span className="font-mono text-xs text-slate-500">#{lead.lead_number}</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          {sm && <StatusBadge status={sm.name} colorToken={sm.color_token} />}
          {lead.is_dnc && <span className="inline-flex items-center gap-1 text-[11px] text-red-400 border border-red-500/40 rounded-full px-2 py-0.5"><ShieldAlert className="h-3 w-3" />DNC</span>}
        </div>
        <div className="grid grid-cols-1 gap-1.5 mt-3 text-sm text-slate-400">
          <span className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{lead.mobile}</span>
          {lead.email && <span className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{lead.email}</span>}
          {(lead.city || lead.pincode) && <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{lead.city} {lead.pincode}</span>}
          {lead.product_interest && <span className="flex items-center gap-2"><Tag className="h-3.5 w-3.5" />{lead.product_interest}</span>}
          <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{fmtDate(lead.created_at)} · {lead.source}{lead.campaign ? ` · ${lead.campaign}` : ""}</span>
        </div>
        {webinars.length > 0 && (
          <button onClick={() => setSheet("webinar")} data-testid="add-to-webinar-button" className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-purple-500/40 text-purple-300 py-2.5 text-sm font-semibold active:bg-purple-500/10">
            <Video className="h-4 w-4" /> Add to Webinar
          </button>
        )}
      </div>

      {/* Timeline + remarks + followups */}
      <section className="mt-5">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Follow-ups</h2>
        <div className="space-y-2">
          {followups.length === 0 && <p className="text-sm text-slate-500">No follow-ups yet.</p>}
          {followups.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/50 p-3">
              <div><div className="text-sm">{fmtDate(f.scheduled_at)}</div>{f.note && <div className="text-xs text-slate-500">{f.note}</div>}</div>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${f.status === "COMPLETED" ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{f.status}</span>
                {f.status === "PENDING" && <button onClick={() => completeFu(f.id)} className="text-primary text-xs font-semibold">Done</button>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-5">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />Remarks</h2>
        <div className="space-y-2">
          {remarks.length === 0 && <p className="text-sm text-slate-500">No remarks yet.</p>}
          {remarks.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
              <div className="text-sm">{r.text}</div>
              <div className="text-[11px] text-slate-500 mt-1">{r.user_name} · {fmtDate(r.created_at)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-5">
        <h2 className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" />Timeline</h2>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 space-y-3">
          {activities.map((a) => (
            <div key={a.id} className="flex gap-3 border-l-2 border-slate-700 pl-3">
              <div className="h-1.5 w-1.5 rounded-full bg-primary -ml-[16px] mt-1.5" />
              <div><div className="text-sm">{a.description}</div><div className="text-[11px] text-slate-500 font-mono">{slugToLabel(a.activity_type)} · {fmtDate(a.created_at)}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* Sticky quick action bar */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-slate-950/95 backdrop-blur-xl border-t border-slate-800">
        <div className="max-w-lg mx-auto px-3 py-2.5 grid grid-cols-5 gap-1.5">
          <button onClick={call} data-testid="lead-call-action-button" className="flex flex-col items-center gap-1 rounded-xl bg-primary text-primary-foreground py-2 text-[11px] font-bold"><Phone className="h-4 w-4" />Call</button>
          <button onClick={() => setSheet("whatsapp")} data-testid="lead-whatsapp-action-button" className="flex flex-col items-center gap-1 rounded-xl border border-emerald-500/40 text-emerald-400 py-2 text-[11px] font-bold"><MessageCircle className="h-4 w-4" />WA</button>
          <button onClick={() => setSheet("status")} data-testid="open-status-sheet" className="flex flex-col items-center gap-1 rounded-xl border border-slate-700 text-slate-200 py-2 text-[11px] font-bold"><Tags className="h-4 w-4" />Status</button>
          <button onClick={() => setSheet("remark")} data-testid="open-remark-sheet" className="flex flex-col items-center gap-1 rounded-xl border border-slate-700 text-slate-200 py-2 text-[11px] font-bold"><MessageSquare className="h-4 w-4" />Remark</button>
          <button onClick={() => setSheet("followup")} data-testid="open-followup-sheet" className="flex flex-col items-center gap-1 rounded-xl border border-slate-700 text-slate-200 py-2 text-[11px] font-bold"><CalendarClock className="h-4 w-4" />F/up</button>
        </div>
      </div>

      {/* STATUS SHEET */}
      <Drawer open={sheet === "status"} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between"><DrawerTitle>Update Status</DrawerTitle><DrawerClose><X className="h-5 w-5 text-slate-400" /></DrawerClose></DrawerHeader>
          <div className="px-4 pb-8 grid grid-cols-2 gap-2 max-h-[60vh] overflow-auto">
            {statuses.map((s) => (
              <button key={s.slug} onClick={() => changeStatus(s.slug)} data-testid={`status-option-${s.slug}`}
                className={`flex items-center justify-between rounded-xl border p-3 text-sm ${lead.current_status === s.slug ? "border-primary bg-primary/10" : "border-slate-700"}`}>
                <StatusBadge status={s.name} colorToken={s.color_token} />
                {lead.current_status === s.slug && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>

      {/* REMARK SHEET */}
      <Drawer open={sheet === "remark"} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between"><DrawerTitle>Add Remark</DrawerTitle><DrawerClose><X className="h-5 w-5 text-slate-400" /></DrawerClose></DrawerHeader>
          <div className="px-4 pb-8 space-y-3">
            <div className="flex flex-wrap gap-2">
              {QUICK_REMARKS.map((r) => (
                <button key={r} onClick={() => addRemark(r)} data-testid={`quick-remark-${r}`} className="rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 active:bg-primary/15">{r}</button>
              ))}
            </div>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} placeholder="Custom remark…" data-testid="remark-input"
              className="w-full rounded-xl bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60" />
            <button onClick={() => addRemark()} data-testid="save-remark" className="w-full rounded-xl bg-primary text-primary-foreground font-bold py-3">Save Remark</button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* FOLLOW-UP SHEET */}
      <Drawer open={sheet === "followup"} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between"><DrawerTitle>Schedule Follow-up</DrawerTitle><DrawerClose><X className="h-5 w-5 text-slate-400" /></DrawerClose></DrawerHeader>
          <div className="px-4 pb-8 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => quickWhen("today")} data-testid="fu-today" className="rounded-xl border border-slate-700 py-3 text-sm font-medium">Today · 11:00</button>
              <button onClick={() => quickWhen("tomorrow")} data-testid="fu-tomorrow" className="rounded-xl border border-slate-700 py-3 text-sm font-medium">Tomorrow · 11:00</button>
            </div>
            <div>
              <label className="text-xs text-slate-500">Custom date & time</label>
              <input type="datetime-local" data-testid="fu-custom"
                value={fuWhen ? new Date(fuWhen.getTime() - fuWhen.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""}
                onChange={(e) => setFuWhen(e.target.value ? new Date(e.target.value) : null)}
                className="w-full rounded-xl bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm mt-1" />
            </div>
            <input value={fuNote} onChange={(e) => setFuNote(e.target.value)} placeholder="Optional note" data-testid="fu-note"
              className="w-full rounded-xl bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm" />
            {fuWhen && <p className="text-xs text-primary">Selected: {fmtDate(fuWhen.toISOString())}</p>}
            <button onClick={saveFollowup} data-testid="save-followup" className="w-full rounded-xl bg-primary text-primary-foreground font-bold py-3">Save Follow-up</button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* WHATSAPP TEMPLATE SHEET */}      <Drawer open={sheet === "whatsapp"} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between"><DrawerTitle>WhatsApp</DrawerTitle><DrawerClose><X className="h-5 w-5 text-slate-400" /></DrawerClose></DrawerHeader>
          <div className="px-4 pb-8 space-y-2">
            <button onClick={() => openWa(null)} data-testid="wa-default" className="w-full flex items-center justify-between rounded-xl border border-emerald-500/40 text-emerald-400 p-3.5 font-semibold">
              Open with default message <MessageCircle className="h-4 w-4" />
            </button>
            <p className="text-xs text-slate-500 pt-2">Templates</p>
            {templates.map((t) => (
              <div key={t.id} data-testid={`wa-template-${t.id}`} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                <div className="font-medium text-sm">{t.name}</div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.body}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => openWa(t.id)} className="flex-1 rounded-lg bg-primary text-primary-foreground py-2 text-xs font-bold flex items-center justify-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />Open</button>
                  <button onClick={() => copyMsg(t.id)} className="rounded-lg border border-slate-700 px-3 py-2 text-xs flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" />Copy</button>
                </div>
              </div>
            ))}
            <p className="text-[11px] text-slate-600 pt-2">CRM आपके लिए message नहीं भेजता — आप WhatsApp में जाकर खुद send करते हैं।</p>
          </div>
        </DrawerContent>
      </Drawer>

      {/* ADD TO WEBINAR SHEET */}
      <Drawer open={sheet === "webinar"} onOpenChange={(o) => !o && setSheet(null)}>
        <DrawerContent className="bg-slate-950 border-slate-800">
          <DrawerHeader className="flex items-center justify-between"><DrawerTitle>Add to Webinar</DrawerTitle><DrawerClose><X className="h-5 w-5 text-slate-400" /></DrawerClose></DrawerHeader>
          <div className="px-4 pb-8 space-y-2">
            {webinars.length === 0 && <p className="text-sm text-slate-500">No upcoming webinars.</p>}
            {webinars.map((w) => (
              <button key={w.id} onClick={() => addToWebinar(w.id, w.name)} data-testid={`webinar-option-${w.id}`}
                className="w-full flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/50 p-3.5 text-left active:bg-purple-500/10">
                <div>
                  <div className="font-medium text-sm">{w.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2"><CalendarClock className="h-3 w-3" />{w.date}{w.start_time ? ` · ${w.start_time}` : ""}{w.host ? ` · ${w.host}` : ""}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-purple-400" />
              </button>
            ))}
            <p className="text-[11px] text-slate-600 pt-2">Lead will be marked "Webinar Ready" and registered for the selected webinar.</p>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
